/**
 * Course overview sidebar.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Box, Grid, Tab, Tabs } from "@mui/material";
import { Icon } from "@iconify/react";
import { useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";

import { VText, VAvatar } from "form";
import { useAsterController } from "context";
import CourseReplayItem from "./course.replay.item";
import CourseVirtualItem from "./course.virtual.item";
import ToInstructor from "pages/common/ToInsructor";
import { pickCourseVirtualSession } from "utils/course";

/**
 * Course metadata, progress, and tab navigation.
 *
 * @param {{ tab: number, setTab: Function }} props
 */
const CourseSidebar = (props) => {
  const { t } = useTranslation("common");

  const { setParentTab } = props;
  const { courseId } = useParams();

  const [controller] = useAsterController();
  const { mobile, pastSessions, upcomingSessions, meetEvents, courses, loadedCourseDetail } = controller;

  const [tab, setTab] = useState(0);

  const virtualSession = useMemo(() => pickCourseVirtualSession(meetEvents, upcomingSessions, courseId), [meetEvents, upcomingSessions, courseId]);
  const pastSession = useMemo(() => (pastSessions.data?.length ? pastSessions.data[0] || {} : {}), [pastSessions.data]);
  const currCourse = useMemo(() => (courses.data || []).find((v) => v.id === courseId) || {}, [courseId, courses.data]);

  const handleSetTabValue = (_, newValue) => setTab(newValue);

  useEffect(() => mobile && setTab(0), [mobile]);

  return (
    <Grid item xs={12} lg={3}>
      <Box sx={{ maxWidth: { lg: "360px" } }} className="mx-auto">
        <Box className="w-full">
          <Tabs orientation="horizontal" value={tab} onChange={handleSetTabValue} className="h-12 v-tabs">
            <Tab label={t("Up coming Virtual Class")} icon={<Icon className="mb-0 h-5 text-3xl flex-shrink-0" icon="heroicons-outline:video-camera" />} className="min-h-0 text-start text-dark flex-row items-start h-12 w-6/12 px-0 normal-case" />
            <Tab label={t("Terminated Sessions")} icon={<Icon className="mb-0 h-5 text-3xl flex-shrink-0" icon="ri:checkbox-multiple-line" />} className="min-h-0 text-start text-dark flex-row items-start h-12 w-6/12 normal-case" />
          </Tabs>
          <Grid container className="my-5">
            <Grid item xs={12} sm={6} md={4} lg={12}>
              {loadedCourseDetail ? (
                <>
                  {tab === 0 && <CourseVirtualItem session={virtualSession} setParentTab={setParentTab} />}
                  {tab === 1 && (
                    <>
                      {pastSession.file_id ? (
                        <CourseReplayItem session={pastSession} setParentTab={setParentTab} />
                      ) : (
                        <Box className="w-full flex justify-center">
                          <VText color="secondary">{t("No Data!")}</VText>
                        </Box>
                      )}
                    </>
                  )}
                </>
              ) : (
                <Box className="rounded-md border border-solid border-gray-200 dark:border-gray-500 p-4">
                  <Box className="relative">
                    <Skeleton className="aspect-[9/5] mb-4" />
                    <Skeleton circle className="w-20 h-20 absolute -bottom-0 left-2" />
                  </Box>
                  <Skeleton count={3} className="mt-2" />
                  <Skeleton className="h-8 mt-4" />
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>

        <Box className="w-full pt-8">
          <VText color="secondary" className="flex items-center gap-2 text-sm">
            <Icon className="mb-0 text-2xl" icon="bx:user-circle" />
            {t("Coach on this course")}
          </VText>
        </Box>

        <Box>
          <Box className="flex items-center mt-5 overflow-hidden">
            <VAvatar src={currCourse.coach_avatar || `${process.env.REACT_APP_S3_ASSET_ENDPOINT}/lms/avatar.png`} className="h-16 w-16" />
            <Box className="ml-4 flex flex-col">
              {loadedCourseDetail ? <VText className="text-lg">{currCourse.coach_first_name || currCourse.coach_last_name}</VText> : <Skeleton className="h-7 w-32" />}
              {loadedCourseDetail ? (
                <VText className="text-lg text-limit-1" color="secondary">
                  {currCourse.coach_email}
                </VText>
              ) : (
                <Skeleton />
              )}
            </Box>
          </Box>
          {loadedCourseDetail ? (
            <VText color="secondary" div className="mt-2">
              {currCourse.coach_bio}
            </VText>
          ) : (
            <Skeleton count={5} className="mt-2" />
          )}
        </Box>

        <ToInstructor />
      </Box>
    </Grid>
  );
};

export default CourseSidebar;
